import dagre from '@dagrejs/dagre';
import type { Schema, Table, TablePosition } from '@hyphen-db-studio/schema-core';

const NODE_WIDTH = 240;
const HEADER_HEIGHT = 40;
const ROW_HEIGHT = 28;
const FOOTER_PADDING = 12;

/** Must match the padding the diagram canvas draws around a group's bounding box, so the
 * layout reserves enough room for it — see DiagramCanvas's groupNodes computation. */
const GROUP_PADDING = 28;
const GROUP_LABEL_HEIGHT = 24;

export function estimateTableHeight(columnCount: number): number {
  return HEADER_HEIGHT + Math.max(columnCount, 1) * ROW_HEIGHT + FOOTER_PADDING;
}

export interface AutoLayoutOptions {
  direction?: 'LR' | 'TB';
  nodeSeparation?: number;
  rankSeparation?: number;
}

function newGraph(direction: 'LR' | 'TB', nodesep: number, ranksep: number, marginx: number, marginy: number) {
  const graph = new dagre.graphlib.Graph();
  graph.setGraph({ rankdir: direction, nodesep, ranksep, marginx, marginy });
  graph.setDefaultEdgeLabel(() => ({}));
  return graph;
}

/**
 * Computes non-overlapping positions for every table using dagre's layered layout
 * algorithm, in two passes:
 *
 * 1. Each group's member tables are laid out in isolation, producing a compact
 *    relative sub-layout (and its bounding size).
 * 2. A top-level graph treats each group as a single meta-node — sized to its
 *    sub-layout PLUS the padding/label strip the canvas actually draws around a group —
 *    and every ungrouped table as its own node, then lays those out together.
 *
 * A flat, group-unaware layout only prevents individual tables from overlapping; on
 * larger schemas the group boxes drawn around clustered tables (a separate, inflated
 * bounding rectangle) would then routinely overlap neighboring tables or other groups,
 * since dagre never knew that extra visual footprint existed. Reserving the inflated
 * size at the top level up front is what actually avoids that.
 */
export function computeAutoLayout(schema: Schema, options: AutoLayoutOptions = {}): Record<string, TablePosition> {
  const direction = options.direction ?? 'LR';
  const nodesep = options.nodeSeparation ?? 60;
  const ranksep = options.rankSeparation ?? 140;

  const membersByGroup = new Map<string, Table[]>();
  for (const table of schema.tables) {
    if (!table.groupId) continue;
    if (!schema.groups.some((g) => g.id === table.groupId)) continue;
    const list = membersByGroup.get(table.groupId) ?? [];
    list.push(table);
    membersByGroup.set(table.groupId, list);
  }

  // Pass 1: compact relative sub-layout per group.
  const subLayouts = new Map<string, { positions: Record<string, TablePosition>; width: number; height: number }>();
  for (const [groupId, members] of membersByGroup) {
    const memberIds = new Set(members.map((t) => t.id));
    const subGraph = newGraph(direction, nodesep, ranksep, 0, 0);
    for (const table of members) {
      subGraph.setNode(table.id, { width: NODE_WIDTH, height: estimateTableHeight(table.columns.length) });
    }
    for (const rel of schema.relationships) {
      if (rel.fromTableId === rel.toTableId) continue;
      if (memberIds.has(rel.fromTableId) && memberIds.has(rel.toTableId)) {
        subGraph.setEdge(rel.fromTableId, rel.toTableId);
      }
    }
    dagre.layout(subGraph);

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    const raw: Record<string, TablePosition> = {};
    for (const table of members) {
      const node = subGraph.node(table.id);
      const left = node.x - node.width / 2;
      const top = node.y - node.height / 2;
      raw[table.id] = { x: left, y: top };
      minX = Math.min(minX, left);
      minY = Math.min(minY, top);
      maxX = Math.max(maxX, left + node.width);
      maxY = Math.max(maxY, top + node.height);
    }
    const positions: Record<string, TablePosition> = {};
    for (const table of members) {
      positions[table.id] = { x: raw[table.id].x - minX, y: raw[table.id].y - minY };
    }
    subLayouts.set(groupId, { positions, width: maxX - minX, height: maxY - minY });
  }

  // Pass 2: top-level layout of {group meta-nodes, sized incl. padding} + ungrouped tables.
  const topGraph = newGraph(direction, nodesep, ranksep, 40, 40);
  const metaIdForTable = (tableId: string): string => {
    const table = schema.tables.find((t) => t.id === tableId);
    if (table?.groupId && subLayouts.has(table.groupId)) return `meta-group:${table.groupId}`;
    return tableId;
  };

  for (const [groupId, layout] of subLayouts) {
    topGraph.setNode(`meta-group:${groupId}`, {
      width: layout.width + GROUP_PADDING * 2,
      height: layout.height + GROUP_PADDING * 2 + GROUP_LABEL_HEIGHT,
    });
  }
  for (const table of schema.tables) {
    if (table.groupId && subLayouts.has(table.groupId)) continue;
    topGraph.setNode(table.id, { width: NODE_WIDTH, height: estimateTableHeight(table.columns.length) });
  }

  const seenEdges = new Set<string>();
  for (const rel of schema.relationships) {
    const from = metaIdForTable(rel.fromTableId);
    const to = metaIdForTable(rel.toTableId);
    if (from === to) continue;
    const key = `${from}->${to}`;
    if (seenEdges.has(key)) continue;
    seenEdges.add(key);
    topGraph.setEdge(from, to);
  }

  dagre.layout(topGraph);

  const positions: Record<string, TablePosition> = {};
  for (const [groupId, layout] of subLayouts) {
    const meta = topGraph.node(`meta-group:${groupId}`);
    if (!meta) continue;
    const originX = meta.x - meta.width / 2 + GROUP_PADDING;
    const originY = meta.y - meta.height / 2 + GROUP_PADDING + GROUP_LABEL_HEIGHT;
    for (const [tableId, rel] of Object.entries(layout.positions)) {
      positions[tableId] = { x: originX + rel.x, y: originY + rel.y };
    }
  }
  for (const table of schema.tables) {
    if (table.groupId && subLayouts.has(table.groupId)) continue;
    const node = topGraph.node(table.id);
    if (!node) continue;
    positions[table.id] = { x: node.x - node.width / 2, y: node.y - node.height / 2 };
  }
  return positions;
}
